// Типы для продвинутой аналитики и машинного обучения
import { EventStatus } from './events-unified';

export interface IAnalyticsDashboard {
  id: string;
  name: string;
  description?: string;
  widgets: IAnalyticsWidget[];
  layout: IDashboardLayout;
  filters: IAnalyticsFilter[];
  dateRange: IDateRange;
  refreshInterval: number;
  isPublic: boolean;
  ownerId: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface IAnalyticsWidget {
  id: string;
  type: AnalyticsWidgetType;
  title: string;
  description?: string;
  chartType: ChartType;
  dataSource: IDataSource;
  config: IWidgetConfig;
  position: IWidgetPosition;
  size: IWidgetSize;
  filters: IAnalyticsFilter[];
  drillDownEnabled: boolean;
  realTimeEnabled: boolean;
  mlEnabled: boolean;
  mlModel?: IMLModel;
}

export interface IMLModel {
  id: string;
  name: string;
  type: MLModelType;
  algorithm: MLAlgorithm;
  accuracy: number;
  lastTrained: Date;
  deployedAt?: Date;
  features: string[];
  predictions: IMLPrediction[];
  config: IMLConfig;
  status: MLModelStatus;
}

export interface IMLPrediction {
  id: string;
  modelId: string;
  input: Record<string, unknown>;
  output: Record<string, unknown>;
  confidence: number;
  timestamp: Date;
  metadata?: Record<string, unknown>;
}

export interface IMLConfig {
  trainingDataSize: number;
  validationSplit: number;
  hyperparameters: Record<string, unknown>;
  featureEngineering: IFeatureEngineering[];
  crossValidation: ICrossValidation;
  preprocessing: IPreprocessing;
}

export interface IFeatureEngineering {
  type: FeatureType;
  column: string;
  transformation: string;
  parameters?: Record<string, unknown>;
}

export interface ICrossValidation {
  enabled: boolean;
  folds: number;
  strategy: 'kfold' | 'stratified' | 'time_series';
  metrics: string[];
}

export interface IPreprocessing {
  scaling: ScalingType;
  encoding: EncodingType;
  missingValues: MissingValueStrategy;
  outliers: OutlierStrategy;
}

export interface IDataSource {
  id: string;
  name: string;
  type: DataSourceType;
  connection: IDataConnection;
  query: string;
  parameters: Record<string, unknown>;
  refreshInterval: number;
  lastUpdated: Date;
  schema: IDataSchema;
}

export interface IDataConnection {
  type: ConnectionType;
  host?: string;
  port?: number;
  database?: string;
  username?: string;
  apiUrl?: string;
  apiKey?: string;
  headers?: Record<string, string>;
}

export interface IDataSchema {
  columns: IDataColumn[];
  primaryKey?: string;
  indexes: string[];
  relationships: IDataRelationship[];
}

export interface IDataColumn {
  name: string;
  type: DataColumnType;
  nullable: boolean;
  defaultValue?: any;
  description?: string;
  constraints?: IColumnConstraint[];
}

export interface IDataRelationship {
  type: RelationshipType;
  fromColumn: string;
  toTable: string;
  toColumn: string;
}

export interface IColumnConstraint {
  type: ConstraintType;
  value?: any;
  message?: string;
}

export interface IAnalyticsFilter {
  id: string;
  field: string;
  operator: FilterOperator;
  value: any;
  label: string;
  type: FilterType;
  options?: IFilterOption[];
  required: boolean;
  visible: boolean;
}

export interface IFilterOption {
  label: string;
  value: any;
  color?: string;
  icon?: string;
}

export interface IDateRange {
  start: Date;
  end: Date;
  preset?: DateRangePreset;
  timezone: string;
}

export interface IDashboardLayout {
  columns: number;
  rowHeight: number;
  margin: [number, number];
  containerPadding: [number, number];
  responsive: boolean;
  breakpoints: Record<string, number>;
}

export interface IWidgetPosition {
  x: number;
  y: number;
  w: number;
  h: number;
  minW?: number;
  minH?: number;
  maxW?: number;
  maxH?: number;
}

export interface IWidgetSize {
  width: number;
  height: number;
  minWidth?: number;
  minHeight?: number;
  maxWidth?: number;
  maxHeight?: number;
}

export interface IWidgetConfig {
  colors: string[];
  theme: string;
  showLegend: boolean;
  showTooltip: boolean;
  showGrid: boolean;
  animation: boolean;
  responsive: boolean;
  customOptions?: Record<string, unknown>;
}

// ─── Domain enums ─────────────────────────────────────────────────────────────

/**
 * Язык ФИЛЬТРА `/analytics/events` — не статус мероприятия.
 *
 * AD-360: это перечисление было вторым словарём статусов, и его словами
 * подписывалась колонка «Статус». Сервер же в поле `status` записи отдаёт
 * каноничный статус сущности (`EventStatus`, см. BE
 * `analytics/services/domain/event-analytics.service.ts`: `status: event.status`),
 * а эти слова понимает ТОЛЬКО как значение параметра фильтра:
 * `active` → `published` с будущей датой, `completed` → `completed` с прошедшей,
 * `cancelled` → `cancelled`. Значения `planned` на сервере нет вовсе
 * (`EventAnalyticsStatus` в BE — `all|active|completed|cancelled`), фильтр с ним
 * молча отдавал невыфильтрованный список — поэтому оно убрано.
 *
 * Статус события для показа берётся из канона `types/events-unified` и
 * подписывается `EVENT_STATUS_LABEL_KEYS`.
 */
export enum EventAnalyticsStatusFilter {
  ACTIVE = 'active',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled',
}

export enum ParticipantStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  VIP = 'vip',
}

export enum ExportFormat {
  CSV = 'csv',
  XLSX = 'xlsx',
  PDF = 'pdf',
}

export enum DateRangePeriod {
  LAST_7 = '7',
  LAST_30 = '30',
  LAST_90 = '90',
  LAST_YEAR = '365',
}

// Аналитика мероприятий
export interface EventAnalyticsFilters {
  eventId?: string;
  dateRange?: DateRangePeriod;
  eventType?: string;
  status?: EventAnalyticsStatusFilter;
  category?: string;
  organizerId?: string;
  page?: number;
  limit?: number;
  startDate?: string;
  endDate?: string;
}

/**
 * Запись списка аналитики мероприятий.
 *
 * AD-360: обязателен только идентификатор. Полный набор полей приходит лишь с
 * платформенного `/analytics/events`; у event-scoped источников
 * (`/event-admin/metrics`, `/organizer/analytics`) большей части этих полей в
 * контракте нет. Пока все поля были обязательными, роутер был ВЫНУЖДЕН
 * заполнять их выдумкой — пустой строкой, нулём и статусом «активно», — и экран
 * печатал это как измеренное значение. Необязательность здесь и есть механизм,
 * которым «нет данных» доходит до экрана вместо подделки.
 */
export interface EventAnalyticsEntry {
  id: string;
  title?: string;
  type?: string;
  /** Каноничный статус события — тот же словарь, что и во всём фронте. */
  status?: EventStatus;
  startDate?: string;
  endDate?: string;
  participantsCount?: number;
  registrationsCount?: number;
  conversionRate?: number;
  revenue?: number;
  averageRating?: number;
  completionRate?: number;
  organizerId?: string;
  organizerName?: string;
  category?: string;
  location?: string;
  capacity?: number;
  attendanceRate?: number;
  engagementScore?: number;
  totalInteractions?: number;
  socialShares?: number;
  feedbackCount?: number;
  averageSessionDuration?: number;
  peakAttendance?: number;
  revenuePerParticipant?: number;
  costPerAcquisition?: number;
  returnOnInvestment?: number;
}

export interface EventAnalyticsPagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

/**
 * Сводные метрики аналитики мероприятий.
 *
 * AD-360: группы и поля необязательны по той же причине, что и в
 * `EventAnalyticsEntry`. Полный набор считает только платформенный
 * `/analytics/events/metrics`; `/event-admin/metrics` не знает ни выручки, ни
 * счёта мероприятий, а `/organizer/metrics` — ни рейтинга, ни вовлечённости.
 * Отсутствующая группа означает «этот источник такого не считает», и карточка
 * обязана сказать это словами, а не показать ноль.
 */
export interface EventAnalyticsMetrics {
  overview?: {
    totalEvents?: number;
    activeEvents?: number;
    completedEvents?: number;
    cancelledEvents?: number;
    upcomingEvents?: number;
  };
  participation?: {
    totalParticipants?: number;
    totalRegistrations?: number;
    averageConversionRate?: number;
    averageAttendanceRate?: number;
    peakAttendance?: number;
  };
  financial?: {
    totalRevenue?: number;
    averageRevenuePerEvent?: number;
    averageRevenuePerParticipant?: number;
    costPerAcquisition?: number;
    returnOnInvestment?: number;
  };
  engagement?: {
    averageRating?: number;
    averageEngagementScore?: number;
    totalInteractions?: number;
    socialShares?: number;
    feedbackCount?: number;
  };
  performance?: {
    averageCompletionRate?: number;
    averageSessionDuration?: number;
    mostPopularCategory?: string;
    mostActiveTime?: string;
    mostActiveDay?: string;
  };
  trends?: {
    eventsByMonth: Array<{ month: string; count: number; revenue: number }>;
    participantsByEvent: Array<{ eventId: string; eventTitle: string; participants: number }>;
    revenueByEvent: Array<{ eventId: string; eventTitle: string; revenue: number }>;
    popularCategories: Array<{ category: string; count: number; revenue: number }>;
  };
}

// Аналитика участников
export interface ParticipantAnalyticsFilters {
  eventId?: string;
  dateRange?: DateRangePeriod;
  status?: ParticipantStatus;
  location?: string;
  page?: number;
  limit?: number;
}

export interface ParticipantAnalyticsEntry {
  id: string;
  name: string;
  email: string;
  avatar?: string;
  registrationDate: string;
  eventsAttended: number;
  totalSpent: number;
  averageRating: number;
  location: string;
  status: ParticipantStatus;
  lastActivity: string;
  eventsRegistered: number;
  eventsCompleted: number;
  averageEventRating: number;
  referralCount: number;
  totalEarnings: number;
  membershipDuration: number;
  engagementScore: number;
}

export interface ParticipantAnalyticsPagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface ParticipantAnalyticsTrends {
  registrationTrend?: Array<{ date: string; count: number }>;
  activityTrend?: Array<{ date: string; activeUsers: number }>;
  revenueTrend?: Array<{ date: string; revenue: number }>;
}

/**
 * Сводные метрики аналитики участников.
 *
 * AD-360: группы и поля необязательны — event-scoped источники
 * (`/event-admin/metrics`, `/organizer/metrics`) считают из всего этого только
 * число участников и, у первого, регистрации по дням. Пока поля были
 * обязательными, роутер добивал ответ нулями, и «удержание 0 %», «выручка 0»,
 * «в среднем 1 мероприятие на участника» выглядели измерениями.
 */
export interface ParticipantAnalyticsMetrics {
  overview?: {
    totalParticipants?: number;
    newParticipants?: number;
    activeParticipants?: number;
    vipParticipants?: number;
    inactiveParticipants?: number;
  };
  engagement?: {
    averageEventsPerParticipant?: number;
    averageSpendingPerParticipant?: number;
    averageRating?: number;
    retentionRate?: number;
    churnRate?: number;
  };
  financial?: {
    totalRevenue?: number;
    averageRevenuePerParticipant?: number;
    topSpenders?: number;
    revenueGrowth?: number;
  };
  activity?: {
    averageSessionDuration?: number;
    averageLoginFrequency?: number;
    mostActiveTime?: string;
    mostActiveDay?: string;
  };
  trends?: ParticipantAnalyticsTrends;
}

export interface ParticipantSegmentation {
  byAge: Array<{ ageGroup: string; count: number; percentage: number }>;
  byLocation: Array<{ location: string; count: number; percentage: number }>;
  byActivity: Array<{ activityLevel: string; count: number; percentage: number }>;
  bySpending: Array<{ spendingLevel: string; count: number; percentage: number }>;
  byEngagement: Array<{ engagementLevel: string; count: number; percentage: number }>;
}

// Аналитика конверсий
export interface ConversionAnalyticsFilters {
  dateRange?: DateRangePeriod;
  eventId?: string;
  funnelType?: string;
}

export interface ConversionFunnelStep {
  id: string;
  eventId: string;
  eventTitle: string;
  step: string;
  stepName: string;
  users: number;
  conversions: number;
  conversionRate: number;
  dropOffRate: number;
  averageTime: number;
}

export interface ConversionAnalyticsMetrics {
  totalVisitors: number;
  totalConversions: number;
  overallConversionRate: number;
  averageConversionTime: number;
  topPerformingEvent: string;
  worstPerformingEvent: string;
  conversionTrend: number;
}

// Аналитика пользователей
export interface UserAnalyticsFilters {
  dateRange?: DateRangePeriod;
  role?: string;
  department?: string;
  status?: string;
  activityLevel?: string;
  page?: number;
  limit?: number;
}

/**
 * Строка списка GET /analytics/users — РОВНО поля `UserAnalyticsItemDto`.
 *
 * AD-93: отсюда убраны двадцать полей, которых сервер не отдавал никогда
 * (`eventsOrganized`, `totalSpent`, `totalEarned`, `referralCount`,
 * `loginFrequency`, `sessionDuration`, `tasksCompleted`, `completionRate`,
 * `socialConnections`, `contentCreated` и прочие): нормализатор подставлял в них
 * нули, и экран печатал эти нули как измеренные величины. Поле `lastLogin`
 * переименовано в `lastLoginDate` — так оно называется в ответе.
 */
export interface UserAnalyticsEntry {
  id: string;
  name: string;
  email: string;
  avatar?: string;
  /** Контекстная роль (`users.role`): admin, manager, user, organizer, … */
  role: string;
  /** Как записано в `users.department`; пустая строка — отдел не указан. */
  department: string;
  status: 'active' | 'inactive' | 'blocked';
  registrationDate: string;
  lastLoginDate: string | null;
  /** Участий за период (окно активности). */
  totalEvents: number;
  eventsAttended: number;
  /** Средняя оценка посещённых мероприятий, 0–5. */
  averageRating: number;
  /** AD-93: шкала 0–100, та же, в которой экран рисует полосу прогресса. */
  engagementScore: number;
  /** AD-92: сервер выводит уровень из числа участий и знает три ступени. */
  activityLevel: 'low' | 'medium' | 'high';
}

export interface UserAnalyticsPagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

/**
 * Метрики GET /analytics/users/metrics — плоский набор `UsersMetricsResponseDto`.
 *
 * AD-93: прежний тип описывал шесть разделов (`overview`, `engagement`,
 * `performance`, `social`, `financial`, `trends`), которых сервер не отдавал
 * никогда. Все шесть карточек экрана читали эти разделы и печатали ноль на
 * полной базе.
 */
export interface UserAnalyticsMetrics {
  totalUsers: number;
  activeUsers: number;
  /** pending + suspended на стороне базы. */
  inactiveUsers: number;
  blockedUsers: number;
  /** Зарегистрированы внутри выбранного периода. */
  newUsers: number;
  /** Участий на человека за период. */
  averageEventsPerUser: number;
  /** Средняя оценка посещённых мероприятий, 0–5. */
  averageRating: number;
  /** Доля посещённых регистраций, 0–100. */
  averageAttendanceRate: number;
  /** Вовлечённость когорты по той же формуле, что и в строке списка, 0–100. */
  averageEngagement: number;
  topDepartments: Array<{ department: string; count: number; percentage: number }>;
  topRoles: Array<{ role: string; count: number; percentage: number }>;
}

/**
 * Сегментация GET /analytics/users/segmentation.
 *
 * AD-93: поле ступени активности называется `level` (клиент читал
 * `activityLevel` и получал undefined), разрезов `byPerformance` и
 * `byEngagement` сервер не отдаёт вовсе, а разрез `byStatus` отдаёт — и он
 * единственный, который экран не показывал.
 */
export interface UserSegmentation {
  byRole: Array<{ role: string; count: number; percentage: number }>;
  byDepartment: Array<{ department: string; count: number; percentage: number }>;
  byActivity: Array<{ level: string; count: number; percentage: number }>;
  byStatus: Array<{ status: string; count: number; percentage: number }>;
}

// Статистика и метрики
export interface IAnalyticsMetrics {
  pageViews: number;
  uniqueVisitors: number;
  bounceRate: number;
  avgSessionDuration: number;
  conversionRate: number;
  revenue: number;
  customMetrics: Record<string, number>;
}

// Дополнительные типы для SupplierAnalyticsDashboard
export interface AnalyticsMetrics {
  // Основные показатели
  totalRevenue: number;
  totalEvents: number;
  totalUsers: number;
  activeUsers: number;
  
  // Дополнительные поля для совместимости
  totalOrders: number;
  customerSatisfaction: number;
  marketGrowth: number;
  
  // Временные периоды
  currentPeriod: {
    revenue: number;
    events: number;
    users: number;
  };
  previousPeriod: {
    revenue: number;
    events: number;
    users: number;
  };
  
  // Показатели роста
  growth: {
    revenue: number;
    events: number;
    users: number;
  };

  // KPI из /analytics/dashboard/metrics
  conversionRate: number;
  engagementGrowth: number;
}

export interface CompetitorAnalysis {
  id: string;
  name: string;
  marketShare: number;
  
  // Дополнительные поля для совместимости
  averagePrice: number;
  strengths: string[];
  weaknesses: string[];
  
  // Основные метрики
  metrics: {
    revenue: number;
    customers: number;
    satisfaction: number;
  };
  
  // Позиционирование
  positioning: {
    quality: number;
    price: number;
    service: number;
  };
}

export interface AIRecommendation {
  id: string;
  title: string;
  description: string;
  category: RecommendationCategory;
  priority: RecommendationPriority;
  
  // Дополнительные поля для совместимости
  impact: 'low' | 'medium' | 'high';
  effort: 'low' | 'medium' | 'high';
  confidence: number; // 0-1
  
  // Метрики
  expectedImpact: {
    revenue: number;
    efficiency: number;
    satisfaction: number;
  };
  
  // Временные рамки
  timeframe: string;
  estimatedROI: number;
  
  // Детали реализации
  implementation: {
    steps: string[];
    resources: string[];
    timeline: string;
  };
}

export interface PredictiveInsight {
  id: string;
  type: InsightType;
  title: string;
  description: string;
  
  // Дополнительные поля для совместимости
  timeframe: string;
  factors: string[];
  
  // Прогноз
  prediction: {
    value: number;
    confidence: number;
    range: {
      min: number;
      max: number;
    };
  };
  
  // Факторы влияния
  influencingFactors: {
    name: string;
    impact: number;
    trend: 'up' | 'down' | 'stable';
  }[];
  
  // Рекомендации
  recommendations: string[];
}

export interface SupplierAnalytics {
  metrics: {
    monthlyRevenue: number;
    totalOrders: number;
    averageOrderValue: number;
    conversionRate: number;
    customerSatisfaction: number;
    marketGrowth: number;
    responseTime: number;
    repeatCustomerRate: number;
    cancellationRate: number;
  };
  performance: Array<{
    date: string;
    revenue: number;
    orders: number;
    satisfaction: number;
    responseTime: number;
  }>;
  recommendations: AIRecommendation[];
  insights: PredictiveInsight[];
  trends: {
    categoryGrowth: number;
    seasonalTrends: Array<{
      month: string;
      demand: number;
      averagePrice: number;
    }>;
    emergingTrends: Array<{
      trend: string;
      impact: 'high' | 'medium' | 'low';
      description: string;
    }>;
  };
  competitors?: Array<{
    id: string;
    name: string;
    marketShare: number;
    rating: number;
    averagePrice: number;
    strengths: string[];
    weaknesses: string[];
    position: 'leader' | 'challenger' | 'follower' | 'niche';
  }>;
}

export interface UserAnalytics {
  totalUsers: number;
  activeUsers: number;
  newUsers: number;
  retentionRate: number;
  averageSessionDuration: number;
  topRegions: Array<{
    region: string;
    users: number;
    percentage: number;
  }>;
  behaviorData: Array<{
    date: string;
    sessions: number;
    pageViews: number;
    bounceRate: number;
    retention?: number[];
  }>;
}

export interface AnalyticsDateRange {
  startDate: string;
  endDate: string;
}

export interface ReportData {
  id: string;
  name: string;
  type: string;
  data: any;
  generatedAt: string;
  format: 'pdf' | 'excel' | 'csv';
}

export interface ChartData {
  name: string;
  value: number;
  x?: number;
  y?: number;
  percent?: number;
}

export interface IPerformanceMetrics {
  loadTime: number;
  renderTime: number;
  interactionTime: number;
  memoryUsage: number;
  errorRate: number;
  apiResponseTime: number;
}

export interface IUserBehaviorAnalytics {
  userId: string;
  sessionId: string;
  events: IUserEvent[];
  funnel: IFunnelStep[];
  cohort: ICohortData;
  segmentation: IUserSegment;
}

export interface IUserEvent {
  type: string;
  timestamp: Date;
  properties: Record<string, unknown>;
  page: string;
  element?: string;
  value?: number;
}

export interface IFunnelStep {
  step: number;
  name: string;
  users: number;
  dropoff: number;
  conversionRate: number;
  avgTime: number;
}

export interface ICohortData {
  cohortDate: Date;
  size: number;
  retention: number[];
  metrics: Record<string, number>;
}

export interface IUserSegment {
  id: string;
  name: string;
  criteria: ISegmentCriteria[];
  size: number;
  value: number;
  behavior: Record<string, unknown>;
}

export interface ISegmentCriteria {
  field: string;
  operator: string;
  value: any;
  logic: 'AND' | 'OR';
}

// Предиктивная аналитика
export interface IPredictiveModel {
  id: string;
  name: string;
  type: PredictiveModelType;
  target: string;
  features: string[];
  algorithm: string;
  performance: IModelPerformance;
  predictions: IPrediction[];
  deployment: IModelDeployment;
}

export interface IModelPerformance {
  accuracy: number;
  precision: number;
  recall: number;
  f1Score: number;
  mse?: number;
  rmse?: number;
  mae?: number;
  r2?: number;
}

export interface IPrediction {
  id: string;
  modelId: string;
  input: Record<string, unknown>;
  output: any;
  confidence: number;
  timestamp: Date;
  actual?: any;
  error?: number;
}

export interface IModelDeployment {
  status: DeploymentStatus;
  version: string;
  endpoint?: string;
  lastDeployed: Date;
  performance: IModelPerformance;
  monitoring: IModelMonitoring;
}

export interface IModelMonitoring {
  enabled: boolean;
  alerts: IModelAlert[];
  metrics: IModelMetrics[];
  driftDetection: IDriftDetection;
}

export interface IModelAlert {
  type: AlertType;
  threshold: number;
  message: string;
  severity: AlertSeverity;
  enabled: boolean;
}

export interface IModelMetrics {
  name: string;
  value: number;
  threshold: number;
  status: MetricStatus;
  timestamp: Date;
}

export interface IDriftDetection {
  enabled: boolean;
  method: DriftMethod;
  threshold: number;
  features: string[];
  lastCheck: Date;
  drift: boolean;
}

// Рекомендательная система
export interface IRecommendationEngine {
  id: string;
  name: string;
  type: RecommendationType;
  algorithm: RecommendationAlgorithm;
  config: IRecommendationConfig;
  performance: IRecommendationPerformance;
  rules: IRecommendationRule[];
}

export interface IRecommendationConfig {
  maxRecommendations: number;
  minScore: number;
  diversity: number;
  freshness: number;
  popularity: number;
  personalizedWeight: number;
}

export interface IRecommendationPerformance {
  precision: number;
  recall: number;
  coverage: number;
  diversity: number;
  novelty: number;
  clickThroughRate: number;
}

export interface IRecommendationRule {
  id: string;
  name: string;
  condition: string;
  action: string;
  weight: number;
  enabled: boolean;
}

export interface IRecommendation {
  id: string;
  userId: string;
  itemId: string;
  score: number;
  reason: string;
  algorithm: string;
  timestamp: Date;
  clicked?: boolean;
  purchased?: boolean;
}

// Enums
export enum AnalyticsWidgetType {
  METRIC = 'metric',
  CHART = 'chart',
  TABLE = 'table',
  MAP = 'map',
  FUNNEL = 'funnel',
  COHORT = 'cohort',
  HEATMAP = 'heatmap',
  PREDICTION = 'prediction',
  RECOMMENDATION = 'recommendation',
  CUSTOM = 'custom',
}

export enum ChartType {
  LINE = 'line',
  BAR = 'bar',
  PIE = 'pie',
  DOUGHNUT = 'doughnut',
  AREA = 'area',
  SCATTER = 'scatter',
  BUBBLE = 'bubble',
  RADAR = 'radar',
  TREEMAP = 'treemap',
  SANKEY = 'sankey',
  WATERFALL = 'waterfall',
  CANDLESTICK = 'candlestick',
}

export enum MLModelType {
  CLASSIFICATION = 'classification',
  REGRESSION = 'regression',
  CLUSTERING = 'clustering',
  ANOMALY_DETECTION = 'anomaly_detection',
  TIME_SERIES = 'time_series',
  NLP = 'nlp',
  RECOMMENDATION = 'recommendation',
  FORECASTING = 'forecasting',
}

export enum MLAlgorithm {
  LINEAR_REGRESSION = 'linear_regression',
  LOGISTIC_REGRESSION = 'logistic_regression',
  RANDOM_FOREST = 'random_forest',
  GRADIENT_BOOSTING = 'gradient_boosting',
  SVM = 'svm',
  NEURAL_NETWORK = 'neural_network',
  K_MEANS = 'k_means',
  DBSCAN = 'dbscan',
  ISOLATION_FOREST = 'isolation_forest',
  ARIMA = 'arima',
  LSTM = 'lstm',
  TRANSFORMER = 'transformer',
}

export enum MLModelStatus {
  TRAINING = 'training',
  TRAINED = 'trained',
  DEPLOYING = 'deploying',
  DEPLOYED = 'deployed',
  ERROR = 'error',
  DEPRECATED = 'deprecated',
}

export enum DataSourceType {
  API = 'api',
  DATABASE = 'database',
  FILE = 'file',
  STREAM = 'stream',
  WEBHOOK = 'webhook',
  MANUAL = 'manual',
}

export enum ConnectionType {
  REST_API = 'rest_api',
  GRAPHQL = 'graphql',
  POSTGRESQL = 'postgresql',
  MYSQL = 'mysql',
  MONGODB = 'mongodb',
  REDIS = 'redis',
  ELASTICSEARCH = 'elasticsearch',
  KAFKA = 'kafka',
  WEBSOCKET = 'websocket',
}

export enum DataColumnType {
  STRING = 'string',
  NUMBER = 'number',
  INTEGER = 'integer',
  BOOLEAN = 'boolean',
  DATE = 'date',
  DATETIME = 'datetime',
  JSON = 'json',
  ARRAY = 'array',
}

export enum RelationshipType {
  ONE_TO_ONE = 'one_to_one',
  ONE_TO_MANY = 'one_to_many',
  MANY_TO_MANY = 'many_to_many',
}

export enum ConstraintType {
  UNIQUE = 'unique',
  NOT_NULL = 'not_null',
  MIN_LENGTH = 'min_length',
  MAX_LENGTH = 'max_length',
  MIN_VALUE = 'min_value',
  MAX_VALUE = 'max_value',
  REGEX = 'regex',
  CUSTOM = 'custom',
}

export enum FilterOperator {
  EQUALS = 'equals',
  NOT_EQUALS = 'not_equals',
  GREATER_THAN = 'greater_than',
  LESS_THAN = 'less_than',
  GREATER_EQUAL = 'greater_equal',
  LESS_EQUAL = 'less_equal',
  CONTAINS = 'contains',
  NOT_CONTAINS = 'not_contains',
  STARTS_WITH = 'starts_with',
  ENDS_WITH = 'ends_with',
  IN = 'in',
  NOT_IN = 'not_in',
  BETWEEN = 'between',
  IS_NULL = 'is_null',
  IS_NOT_NULL = 'is_not_null',
}

export enum FilterType {
  TEXT = 'text',
  NUMBER = 'number',
  DATE = 'date',
  SELECT = 'select',
  MULTISELECT = 'multiselect',
  BOOLEAN = 'boolean',
  RANGE = 'range',
  DATE_RANGE = 'date_range',
}

export enum DateRangePreset {
  TODAY = 'today',
  YESTERDAY = 'yesterday',
  LAST_7_DAYS = 'last_7_days',
  LAST_30_DAYS = 'last_30_days',
  LAST_90_DAYS = 'last_90_days',
  THIS_MONTH = 'this_month',
  LAST_MONTH = 'last_month',
  THIS_QUARTER = 'this_quarter',
  LAST_QUARTER = 'last_quarter',
  THIS_YEAR = 'this_year',
  LAST_YEAR = 'last_year',
  CUSTOM = 'custom',
}

export enum FeatureType {
  NUMERICAL = 'numerical',
  CATEGORICAL = 'categorical',
  DATETIME = 'datetime',
  TEXT = 'text',
  BOOLEAN = 'boolean',
}

export enum ScalingType {
  NONE = 'none',
  STANDARD = 'standard',
  MIN_MAX = 'min_max',
  ROBUST = 'robust',
  QUANTILE = 'quantile',
}

export enum EncodingType {
  NONE = 'none',
  ONE_HOT = 'one_hot',
  LABEL = 'label',
  TARGET = 'target',
  BINARY = 'binary',
}

export enum MissingValueStrategy {
  DROP = 'drop',
  MEAN = 'mean',
  MEDIAN = 'median',
  MODE = 'mode',
  FORWARD_FILL = 'forward_fill',
  BACKWARD_FILL = 'backward_fill',
  INTERPOLATE = 'interpolate',
  CONSTANT = 'constant',
}

export enum OutlierStrategy {
  NONE = 'none',
  IQR = 'iqr',
  Z_SCORE = 'z_score',
  ISOLATION_FOREST = 'isolation_forest',
  CLIP = 'clip',
  REMOVE = 'remove',
}

export enum PredictiveModelType {
  CHURN_PREDICTION = 'churn_prediction',
  DEMAND_FORECASTING = 'demand_forecasting',
  PRICE_OPTIMIZATION = 'price_optimization',
  CUSTOMER_LIFETIME_VALUE = 'customer_lifetime_value',
  FRAUD_DETECTION = 'fraud_detection',
  SENTIMENT_ANALYSIS = 'sentiment_analysis',
  MARKET_BASKET = 'market_basket',
  REVENUE_PREDICTION = 'revenue_prediction',
}

export enum DeploymentStatus {
  PENDING = 'pending',
  DEPLOYING = 'deploying',
  DEPLOYED = 'deployed',
  FAILED = 'failed',
  STOPPED = 'stopped',
}

export enum AlertType {
  ACCURACY_DROP = 'accuracy_drop',
  PREDICTION_DRIFT = 'prediction_drift',
  DATA_DRIFT = 'data_drift',
  PERFORMANCE_DEGRADATION = 'performance_degradation',
  HIGH_ERROR_RATE = 'high_error_rate',
  CUSTOM = 'custom',
}

export enum AlertSeverity {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  CRITICAL = 'critical',
}

export enum MetricStatus {
  NORMAL = 'normal',
  WARNING = 'warning',
  CRITICAL = 'critical',
}

export enum DriftMethod {
  PSI = 'psi',
  KS_TEST = 'ks_test',
  CHI_SQUARE = 'chi_square',
  JENSEN_SHANNON = 'jensen_shannon',
  WASSERSTEIN = 'wasserstein',
}

export enum RecommendationType {
  COLLABORATIVE_FILTERING = 'collaborative_filtering',
  CONTENT_BASED = 'content_based',
  HYBRID = 'hybrid',
  MATRIX_FACTORIZATION = 'matrix_factorization',
  DEEP_LEARNING = 'deep_learning',
  ASSOCIATION_RULES = 'association_rules',
}

export enum RecommendationAlgorithm {
  USER_BASED_CF = 'user_based_cf',
  ITEM_BASED_CF = 'item_based_cf',
  SVD = 'svd',
  NMF = 'nmf',
  NEURAL_CF = 'neural_cf',
  AUTOENCODERS = 'autoencoders',
  APRIORI = 'apriori',
  FP_GROWTH = 'fp_growth',
}

export enum RecommendationCategory {
  PERFORMANCE = 'performance',
  MARKETING = 'marketing',
  OPERATIONS = 'operations',
  FINANCIAL = 'financial',
  CUSTOMER = 'customer',
  TECHNICAL = 'technical'
}

export enum RecommendationPriority {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  CRITICAL = 'critical'
}

export enum InsightType {
  TREND = 'trend',
  ANOMALY = 'anomaly',
  FORECAST = 'forecast',
  CORRELATION = 'correlation',
  RECOMMENDATION = 'recommendation'
}

// Типы для экспорта и отчетов
export interface IReportConfig {
  id: string;
  name: string;
  description?: string;
  type: ReportType;
  format: ReportFormat;
  schedule: IReportSchedule;
  recipients: string[];
  filters: IAnalyticsFilter[];
  widgets: string[];
  template?: string;
}

export interface IReportSchedule {
  enabled: boolean;
  frequency: ReportFrequency;
  time: string;
  timezone: string;
  daysOfWeek?: number[];
  daysOfMonth?: number[];
}

export enum ReportType {
  DASHBOARD = 'dashboard',
  SUMMARY = 'summary',
  DETAILED = 'detailed',
  CUSTOM = 'custom',
}

export enum ReportFormat {
  PDF = 'pdf',
  EXCEL = 'excel',
  CSV = 'csv',
  JSON = 'json',
  HTML = 'html',
}

export enum ReportFrequency {
  REAL_TIME = 'real_time',
  HOURLY = 'hourly',
  DAILY = 'daily',
  WEEKLY = 'weekly',
  MONTHLY = 'monthly',
  QUARTERLY = 'quarterly',
  YEARLY = 'yearly',
}

// Типы для обнаружения аномалий
export interface IAnomalyDetection {
  id: string;
  widgetId: string;
  dataSource: string;
  metric: string;
  algorithm: AnomalyAlgorithm;
  settings: IAnomalySettings;
  results: IAnomalyResult[];
  status: AnomalyDetectionStatus;
  createdAt: Date;
  lastRunAt?: Date;
  nextRunAt?: Date;
}

export interface IAnomalySettings {
  algorithm: AnomalyAlgorithm;
  sensitivity: number;
  outlierStrategy: OutlierStrategy;
  alertEnabled: boolean;
  minSeverity: AnomalySeverity;
  lookbackPeriod: number;
  retrainingInterval: number;
  parameters?: Record<string, unknown>;
}

export interface IAnomalyResult {
  timestamp: Date;
  value: number;
  predicted: number;
  anomaly: boolean;
  severity: AnomalySeverity;
  confidence: number;
  explanation?: string;
  metadata?: Record<string, unknown>;
}

export interface IAnomalyAlert {
  id: string;
  anomalyId: string;
  severity: AnomalySeverity;
  message: string;
  timestamp: Date;
  acknowledged: boolean;
  acknowledgedBy?: string;
  acknowledgedAt?: Date;
  resolved: boolean;
  resolvedAt?: Date;
  dismissed: boolean;
  dismissedAt?: Date;
  dismissalReason?: string;
  note?: string;
  resolutionNote?: string;
  widgetId?: string;
}

export enum AnomalyAlgorithm {
  ISOLATION_FOREST = 'isolation_forest',
  ONE_CLASS_SVM = 'one_class_svm',
  LOCAL_OUTLIER_FACTOR = 'local_outlier_factor',
  DBSCAN = 'dbscan',
  STATISTICAL = 'statistical',
  MOVING_AVERAGE = 'moving_average',
  SEASONAL_DECOMPOSITION = 'seasonal_decomposition',
  AUTOENCODER = 'autoencoder',
}

// Типы для корреляционного анализа
export interface ICorrelationPoint {
  x: number; // основная метрика
  y: number; // коррелирующая метрика
  timestamp: string;
  anomaly: boolean;
  severity: AnomalySeverity;
  correlation?: number; // коэффициент корреляции
  confidence?: number;
}

export interface ICorrelationData {
  points: ICorrelationPoint[];
  statistics: {
    correlationCoefficient: number; // -1 до 1
    totalPoints: number;
    anomalies: number;
    anomalyRate: number;
  };
}

// Типы для массовых операций с алертами
export interface AcknowledgeAllAlertsRequest {
  widgetId?: string;
  severity?: AnomalySeverity;
  filter?: {
    acknowledged?: boolean;
    resolved?: boolean;
  };
}

export interface AcknowledgeAllAlertsResponse {
  acknowledgedCount: number;
  acknowledgedIds: string[];
}

export interface ResolveAllAlertsRequest {
  widgetId?: string;
  severity?: AnomalySeverity;
  filter?: {
    acknowledged?: boolean;
    resolved?: boolean;
  };
}

export interface ResolveAllAlertsResponse {
  resolvedCount: number;
  resolvedIds: string[];
}

// Типы для пагинации алертов
export interface GetAnomalyAlertsResponse {
  items: IAnomalyAlert[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}

export enum AnomalySeverity {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  CRITICAL = 'critical',
}

export enum AnomalyDetectionStatus {
  IDLE = 'idle',
  RUNNING = 'running',
  TRAINING = 'training',
  ERROR = 'error',
  PAUSED = 'paused',
}